import { ExternalLinkDTO } from "@/api/external-link";
import i18n, { SupportedLocale } from "@/i18n";
import {
  computeFieldDimensionsArtwork,
  computeFieldMultipleprintRelatedAuthors,
  computeFieldSignatureArtwork,
  computeFieldSupportTechniqueArtwork,
  computeFieldTirageDimensions,
  pickSculptureEditionTechniqueLine,
} from "@/lib/computeFields";
import {
  Artwork,
  Auction,
  Audiovisual,
  Author,
  Bibliography,
  Document,
  Exhibition,
  Multipleprint,
  Multiplesample,
  Photography,
  SculptureEdition,
  Tirage,
  Writing,
} from "@/lib/types";
import {
  decodeHtmlEntities,
  extractHtmlText,
  parseJsonOrUndefined,
} from "@/lib/utils";
import { TFunction } from "i18next";
import { RelatedLinkGridCardShell } from "./RelatedLinkGridCardShell";

const bodyText =
  "text-[#090909] text-xs font-normal font-['Instrument Sans'] leading-[14.40px]";
const titleText =
  "text-[#090909] text-base text-left font-semibold font-['Instrument Sans'] leading-tight";
const mutedLabel =
  "text-slate-600 text-xs font-normal font-['Instrument Sans'] leading-[14.40px]";
const valueStrong =
  "text-[#090909] text-xs text-left font-semibold font-['Instrument Sans'] leading-[14.40px]";
const safeText = (value?: string | null) => decodeHtmlEntities(value ?? "");
const safeRichText = (value?: string | null) =>
  safeText(extractHtmlText(value ?? ""));

type GridCardClickProps = {
  onTextClick?: () => void;
};

export function ArtworkLinkGridCard({
  artwork,
  t,
  onTextClick,
}: {
  artwork: Artwork;
  t: TFunction;
} & GridCardClickProps) {
  const authorLabel =
    artwork.relatedAuthor && artwork.relatedAuthor.length > 0
      ? `${artwork.relatedAuthor[0].artistesAuteursDescriptionPrenom || ""} ${artwork.relatedAuthor[0].artistesAuteursDescriptionNom || ""}`.trim()
      : t("unknownAuthor");

  return (
    <RelatedLinkGridCardShell
      attachments={artwork.attachments ?? []}
      imageDomain="artwork"
      imageAlt={
        artwork.oeuvresDescriptionTitre ||
        artwork.oeuvresDescriptionNumeroEnregistrement ||
        ""
      }
      detailPath={`/edit/artwork/${artwork.id}`}
      onTextClick={onTextClick}
      listItemId={artwork.id}
    >
      <div className="self-stretch min-h-[30px] py-2 flex-col justify-start items-start gap-1 flex">
        <div>
          <span className={mutedLabel}>
            {t("oeuvresDescriptionNumeroEnregistrement")}{" "}
          </span>
          <span className={valueStrong}>
            {safeText(artwork.oeuvresDescriptionNumeroEnregistrement)}
          </span>
        </div>
      </div>
      <div className="self-stretch min-h-[108px] py-2 flex-col justify-start items-start gap-1 flex">
        <p className={`text-start self-stretch ${bodyText}`}>
          {safeText(authorLabel)}
        </p>
        <p className="text-left leading-tight">
          <span className={`${titleText} w-full`}>
            {safeText(artwork.oeuvresDescriptionTitre)},{" "}
          </span>
          <span
            className={`text-[#090909] text-base text-left font-normal font-['Instrument Sans'] leading-tight`}
          >
            {safeText(artwork.oeuvresDescriptionDateDeCreation)}
          </span>
        </p>
        <div
          className={bodyText}
          dangerouslySetInnerHTML={{
            __html: computeFieldDimensionsArtwork(
              {
                oeuvresDescriptionDimensionsOeuvreRaw:
                  artwork.oeuvresDescriptionDimensionsOeuvreRaw,
                oeuvresDescriptionDimensionsOeuvreInconnues:
                  artwork.oeuvresDescriptionDimensionsOeuvreInconnues,
                oeuvresDescriptionDimensionsOeuvre: parseJsonOrUndefined(
                  artwork.oeuvresDescriptionDimensionsOeuvre,
                ),
              },
              { language: i18n.language as SupportedLocale },
            ),
          }}
        />
        <p className={`text-start self-stretch ${bodyText}`}>
          {safeText(
            computeFieldSupportTechniqueArtwork(artwork, {
              language: i18n.language as SupportedLocale,
            }),
          )}
        </p>
        <p className={`text-start self-stretch min-h-[15px] ${bodyText}`}>
          {safeText(
            computeFieldSignatureArtwork(artwork, {
              language: i18n.language as SupportedLocale,
            }),
          )}
        </p>
      </div>
    </RelatedLinkGridCardShell>
  );
}

export function TirageLinkGridCard({
  tirage,
  t,
  onTextClick,
}: {
  tirage: Tirage;
  t: TFunction;
} & GridCardClickProps) {
  const typeLabel =
    tirage.tirageDescriptionType &&
    Object.keys(tirage.tirageDescriptionType).length > 0
      ? tirage.tirageDescriptionType[i18n.language as SupportedLocale] || ""
      : "";

  return (
    <RelatedLinkGridCardShell
      attachments={tirage.attachments ?? []}
      imageDomain="tirage"
      imageAlt={
        tirage.tirageDescriptionNumeroEnregistrement ||
        tirage.tirageDescriptionNumeroInventaire ||
        ""
      }
      detailPath={tirage.artworkId ? `/edit/artwork/${tirage.artworkId}` : "#"}
      onTextClick={onTextClick}
    >
      <div className="self-stretch min-h-[30px] py-2 flex-col justify-start items-start gap-1 flex">
        <div>
          <span className={mutedLabel}>
            {t("tirageDescriptionNumeroEnregistrement")}{" "}
          </span>
          <span className={valueStrong}>
            {safeText(tirage.tirageDescriptionNumeroEnregistrement)}
          </span>
        </div>
      </div>
      <div className="self-stretch min-h-[108px] py-2 flex-col justify-start items-start gap-1 flex">
        {typeLabel ? (
          <p className={`text-start self-stretch ${bodyText}`}>
            {safeText(typeLabel)}
          </p>
        ) : null}
        <p className="text-left leading-tight">
          <span className={`${titleText} w-full`}>
            {safeText(tirage.tirageDescriptionNumeroInventaire)}
          </span>
          {tirage.tirageDescriptionDateCreation ? (
            <span
              className={`text-[#090909] text-base text-left font-normal font-['Instrument Sans'] leading-tight`}
            >
              {", "}
              {safeText(tirage.tirageDescriptionDateCreation)}
            </span>
          ) : null}
        </p>
        {tirage.tirageDescriptionDimensionsWithMargins ? (
          <div
            className={bodyText}
            dangerouslySetInnerHTML={{
              __html: computeFieldTirageDimensions(
                {
                  unknownDimensions: false,
                  ...(parseJsonOrUndefined(
                    tirage.tirageDescriptionDimensionsWithMargins,
                  ) ?? {}),
                },
                { language: i18n.language as SupportedLocale },
              ),
            }}
          />
        ) : null}
      </div>
    </RelatedLinkGridCardShell>
  );
}

export function SculptureEditionLinkGridCard({
  item,
  t,
  onTextClick,
}: {
  item: SculptureEdition;
  t: TFunction;
} & GridCardClickProps) {
  const technique = pickSculptureEditionTechniqueLine(item, {
    language: i18n.language as SupportedLocale,
  });

  return (
    <RelatedLinkGridCardShell
      attachments={item.attachments ?? []}
      imageDomain="sculptureEdition"
      imageAlt={
        item.sculptureEditionNumeroEnregistrement ||
        item.sculptureEditionNumeroInventaire ||
        ""
      }
      detailPath="#"
      onTextClick={onTextClick}
    >
      <div className="self-stretch min-h-[30px] py-2 flex-col justify-start items-start gap-1 flex">
        <div>
          <span className={mutedLabel}>
            {t("sculpture.form.sculptureEditionNumeroEnregistrement")}{" "}
          </span>
          <span className={valueStrong}>
            {safeText(item.sculptureEditionNumeroEnregistrement)}
          </span>
        </div>
      </div>
      <div className="self-stretch min-h-[108px] py-2 flex-col justify-start items-start gap-1 flex">
        {technique ? (
          <p className={`text-start self-stretch ${bodyText}`}>
            {safeText(technique)}
          </p>
        ) : null}
        {item.sculptureEditionDateRealisation ? (
          <p className={`text-start self-stretch ${bodyText}`}>
            {safeText(item.sculptureEditionDateRealisation)}
          </p>
        ) : null}
      </div>
    </RelatedLinkGridCardShell>
  );
}

export function MultipleprintLinkGridCard({
  item,
  t,
  onTextClick,
}: {
  item: Multipleprint;
  t: TFunction;
} & GridCardClickProps) {
  const authors = computeFieldMultipleprintRelatedAuthors(item, {
    language: i18n.language as SupportedLocale,
  });

  return (
    <RelatedLinkGridCardShell
      attachments={item.attachments ?? []}
      imageDomain="multipleprint"
      imageAlt={
        item.estampesMultiplesDescriptionTitre ||
        item.estampesMultiplesDescriptionNumeroEnregistrement ||
        ""
      }
      detailPath={`/edit/multipleprint/${item.id}`}
      onTextClick={onTextClick}
      listItemId={item.id}
    >
      <div className="self-stretch min-h-[30px] py-2 flex-col justify-start items-start gap-1 flex">
        <div>
          <span className={mutedLabel}>
            {t("estampesMultiplesDescriptionNumeroEnregistrement")}{" "}
          </span>
          <span className={valueStrong}>
            {safeText(item.estampesMultiplesDescriptionNumeroEnregistrement)}
          </span>
        </div>
      </div>
      <div className="self-stretch min-h-[108px] py-2 flex-col justify-start items-start gap-1 flex">
        {authors ? (
          <p className={`text-start self-stretch ${bodyText}`}>
            {safeText(authors)}
          </p>
        ) : null}
        <p className={`text-left leading-tight ${titleText} w-full`}>
          {safeText(item.estampesMultiplesDescriptionTitre)}
        </p>
        {item.estampesMultiplesDescriptionDateDeCreation ? (
          <p className={`text-start self-stretch ${bodyText}`}>
            {safeText(item.estampesMultiplesDescriptionDateDeCreation)}
          </p>
        ) : null}
      </div>
    </RelatedLinkGridCardShell>
  );
}

export function MultiplesampleLinkGridCard({
  item,
  t,
  onTextClick,
}: {
  item: Multiplesample;
  t: TFunction;
} & GridCardClickProps) {
  return (
    <RelatedLinkGridCardShell
      attachments={item.attachments ?? []}
      imageDomain="multiplesample"
      imageAlt={
        item.exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement ||
        item.exemplaireDansEstampesMultiplesDescriptionNumero ||
        ""
      }
      detailPath="#"
      onTextClick={onTextClick}
    >
      <div className="self-stretch min-h-[30px] py-2 flex-col justify-start items-start gap-1 flex">
        <div>
          <span className={mutedLabel}>
            {t("exemplaireDansEstampesMultiplesDescriptionNumero")}{" "}
          </span>
          <span className={valueStrong}>
            {safeText(item.exemplaireDansEstampesMultiplesDescriptionNumero)}
          </span>
        </div>
      </div>
      <div className="self-stretch min-h-[108px] py-2 flex-col justify-start items-start gap-1 flex">
        <p className={`text-left leading-tight ${titleText} w-full`}>
          {safeText(
            item.exemplaireDansEstampesMultiplesDescriptionNumeroInventaire,
          )}
        </p>
        <p className={`text-start self-stretch ${bodyText}`}>
          {safeText(
            item.exemplaireDansEstampesMultiplesDescriptionLocalisation,
          )}
        </p>
      </div>
    </RelatedLinkGridCardShell>
  );
}

export function ExhibitionLinkGridCard({
  item,
  t,
  onTextClick,
}: {
  item: Exhibition;
  t: TFunction;
} & GridCardClickProps) {
  return (
    <RelatedLinkGridCardShell
      attachments={item.attachments ?? []}
      imageDomain="exhibition"
      imageAlt={
        item.expositionsDescriptionTitre ||
        item.expositionsDescriptionNumeroExpo
      }
      detailPath={`/edit/exhibition/${item.id}`}
      onTextClick={onTextClick}
      listItemId={item.id}
    >
      <div className="self-stretch min-h-[30px] py-2 flex-col justify-start items-start gap-1 flex">
        <div>
          <span className={mutedLabel}>
            {t("expositionsDescriptionNumeroExpo")}{" "}
          </span>
          <span className={valueStrong}>
            {safeText(item.expositionsDescriptionNumeroExpo)}
          </span>
        </div>
      </div>
      <div className="self-stretch min-h-[108px] py-2 flex-col justify-start items-start gap-1 flex">
        <p
          className={`text-start self-stretch ${bodyText} line-clamp-4`}
          dangerouslySetInnerHTML={{
            __html: item.expositionsDescriptionResume ?? "",
          }}
        />
      </div>
    </RelatedLinkGridCardShell>
  );
}

export function BibliographyLinkGridCard({
  item,
  t,
  onTextClick,
}: {
  item: Bibliography;
  t: TFunction;
} & GridCardClickProps) {
  return (
    <RelatedLinkGridCardShell
      attachments={item.attachments ?? []}
      imageDomain="bibliography"
      imageAlt={
        item.bibliographieDescriptionTitre ||
        item.bibliographieDescriptionNumeroBibliographie
      }
      detailPath={`/edit/bibliography/${item.id}`}
      onTextClick={onTextClick}
      listItemId={item.id}
    >
      <div className="self-stretch min-h-[30px] py-2 flex-col justify-start items-start gap-1 flex">
        <div>
          <span className={mutedLabel}>
            {t("bibliographieDescriptionNumeroBibliographie")}{" "}
          </span>
          <span className={valueStrong}>
            {safeText(item.bibliographieDescriptionNumeroBibliographie)}
          </span>
        </div>
      </div>
      <div className="self-stretch min-h-[108px] py-2 flex-col justify-start items-start gap-1 flex">
        <p
          className={`text-start self-stretch ${bodyText} line-clamp-4`}
          dangerouslySetInnerHTML={{
            __html:
              item.bibliographieDescriptionResumeCourt ||
              item.bibliographieDescriptionResumeLong ||
              "",
          }}
        />
      </div>
    </RelatedLinkGridCardShell>
  );
}

export function AuctionLinkGridCard({
  item,
  t,
  onTextClick,
}: {
  item: Auction;
  t: TFunction;
} & GridCardClickProps) {
  return (
    <RelatedLinkGridCardShell
      attachments={item.attachments ?? []}
      imageDomain="auction"
      imageAlt={
        item.ventesDescriptionTitre || item.ventesDescriptionNumeroVente
      }
      detailPath={`/edit/auction/${item.id}`}
      onTextClick={onTextClick}
      listItemId={item.id}
    >
      <div className="self-stretch min-h-[30px] py-2 flex-col justify-start items-start gap-1 flex">
        <div>
          <span className={mutedLabel}>
            {t("ventesDescriptionNumeroVente")}{" "}
          </span>
          <span className={valueStrong}>
            {safeText(item.ventesDescriptionNumeroVente)}
          </span>
        </div>
      </div>
      <div className="self-stretch min-h-[108px] py-2 flex-col justify-start items-start gap-1 flex">
        <p className={`text-start self-stretch ${titleText} line-clamp-3`}>
          {safeText(item.ventesDescriptionTitre)}
        </p>
        <p
          className={`text-start self-stretch ${bodyText} line-clamp-4`}
          dangerouslySetInnerHTML={{
            __html: item.ventesDescriptionResume ?? "",
          }}
        />
      </div>
    </RelatedLinkGridCardShell>
  );
}

export function AudiovisualLinkGridCard({
  item,
  t,
  onTextClick,
}: {
  item: Audiovisual;
  t: TFunction;
} & GridCardClickProps) {
  return (
    <RelatedLinkGridCardShell
      attachments={item.attachments ?? []}
      imageDomain="audiovisual"
      imageAlt={
        item.audiovisuelDescriptionTitre ||
        item.audiovisuelDescriptionNumeroEnregistrement
      }
      detailPath={`/edit/audiovisual/${item.id}`}
      onTextClick={onTextClick}
      listItemId={item.id}
    >
      <div className="self-stretch min-h-[30px] py-2 flex-col justify-start items-start gap-1 flex">
        <div>
          <span className={mutedLabel}>
            {t("audiovisuelDescriptionNumeroEnregistrement")}{" "}
          </span>
          <span className={valueStrong}>
            {safeText(item.audiovisuelDescriptionNumeroEnregistrement)}
          </span>
        </div>
      </div>
      <div className="self-stretch min-h-[108px] py-2 flex-col justify-start items-start gap-1 flex">
        <p
          className={`text-start self-stretch ${bodyText} line-clamp-4`}
          dangerouslySetInnerHTML={{
            __html: item.audiovisuelDescriptionResume ?? "",
          }}
        />
      </div>
    </RelatedLinkGridCardShell>
  );
}

export function DocumentLinkGridCard({
  item,
  t,
  onTextClick,
}: {
  item: Document;
  t: TFunction;
} & GridCardClickProps) {
  return (
    <RelatedLinkGridCardShell
      attachments={item.attachments ?? []}
      imageDomain="document"
      imageAlt={
        item.documentsDescriptionTitre ||
        item.documentsDescriptionNumeroEnregistrement
      }
      detailPath={`/edit/document/${item.id}`}
      onTextClick={onTextClick}
      listItemId={item.id}
    >
      <div className="self-stretch min-h-[30px] py-2 flex-col justify-start items-start gap-1 flex">
        <div>
          <span className={mutedLabel}>
            {t("documentsDescriptionNumeroEnregistrement")}{" "}
          </span>
          <span className={valueStrong}>
            {safeText(item.documentsDescriptionNumeroEnregistrement)}
          </span>
        </div>
      </div>
      <div className="self-stretch min-h-[108px] py-2 flex-col justify-start items-start gap-1 flex">
        <p className={`text-start self-stretch ${titleText} line-clamp-2`}>
          {safeText(item.documentsDescriptionTitre)}
        </p>
        <p className={`text-start self-stretch ${bodyText}`}>
          {safeText(item.documentsDescriptionDate)}
        </p>
      </div>
    </RelatedLinkGridCardShell>
  );
}

export function WritingLinkGridCard({
  item,
  t,
  onTextClick,
}: {
  item: Writing;
  t: TFunction;
} & GridCardClickProps) {
  return (
    <RelatedLinkGridCardShell
      attachments={item.attachments ?? []}
      imageDomain="writing"
      imageAlt={
        item.ecritsDescriptionTitre ||
        item.ecritsDescriptionNumeroEnregistrement
      }
      detailPath={`/edit/writing/${item.id}`}
      onTextClick={onTextClick}
      listItemId={item.id}
    >
      <div className="self-stretch min-h-[30px] py-2 flex-col justify-start items-start gap-1 flex">
        <div>
          <span className={mutedLabel}>
            {t("ecritsDescriptionNumeroEnregistrement")}{" "}
          </span>
          <span className={valueStrong}>
            {safeText(item.ecritsDescriptionNumeroEnregistrement)}
          </span>
        </div>
      </div>
      <div className="self-stretch min-h-[108px] py-2 flex-col justify-start items-start gap-1 flex">
        <p className={`text-start self-stretch ${titleText}`}>
          {safeText(item.ecritsDescriptionTitre)}
        </p>
        {item.ecritsDescriptionDate ? (
          <p className={`text-start self-stretch ${bodyText}`}>
            {safeText(item.ecritsDescriptionDate)}
          </p>
        ) : null}
      </div>
    </RelatedLinkGridCardShell>
  );
}

export function PhotographyLinkGridCard({
  item,
  t,
  onTextClick,
}: {
  item: Photography;
  t: TFunction;
} & GridCardClickProps) {
  const rawPhotographyTitle = item.photosDescriptionTitre ?? "";
  const normalizedPhotographyTitle = safeRichText(rawPhotographyTitle);

  return (
    <RelatedLinkGridCardShell
      attachments={item.attachments ?? []}
      imageDomain="photography"
      imageAlt={
        item.photosDescriptionTitre ||
        item.photosDescriptionNumeroEnregistrement
      }
      detailPath={`/edit/photography/${item.id}`}
      onTextClick={onTextClick}
      listItemId={item.id}
    >
      <div className="self-stretch min-h-[30px] py-2 flex-col justify-start items-start gap-1 flex">
        <div>
          <span className={mutedLabel}>
            {t("photosDescriptionNumeroEnregistrement")}{" "}
          </span>
          <span className={valueStrong}>
            {safeText(item.photosDescriptionNumeroEnregistrement)}
          </span>
        </div>
      </div>
      <div className="self-stretch min-h-[108px] py-2 flex-col justify-start items-start gap-1 flex">
        {item.photosDescriptionTitre ? (
          <p className={`text-start self-stretch ${titleText} line-clamp-2`}>
            {normalizedPhotographyTitle}
          </p>
        ) : null}
        {item.photosDescriptionDate ? (
          <p className={`text-start self-stretch ${bodyText}`}>
            {safeText(item.photosDescriptionDate)}
          </p>
        ) : null}
      </div>
    </RelatedLinkGridCardShell>
  );
}

export function ExternalLinkGridCard({
  item,
  t,
  onTextClick,
}: {
  item: ExternalLinkDTO;
  t: TFunction;
} & GridCardClickProps) {
  const title = safeText(item.name) || t("name");
  return (
    <RelatedLinkGridCardShell
      attachments={[]}
      imageDomain="document"
      imageAlt={title}
      detailPath="#"
      onTextClick={onTextClick}
    >
      <div className="self-stretch min-h-[30px] py-2 flex-col justify-start items-start gap-1 flex">
        <p className={`${titleText} w-full line-clamp-2`}>{title}</p>
      </div>
      <div className="self-stretch min-h-[108px] py-2 flex-col justify-start items-start gap-1 flex">
        <p className={`${bodyText} line-clamp-3 break-all`}>
          {safeText(item.url)}
        </p>
        {item.dateConsultation ? (
          <p className={mutedLabel}>{safeText(item.dateConsultation)}</p>
        ) : null}
      </div>
    </RelatedLinkGridCardShell>
  );
}

export function AuthorLinkGridCard({
  item,
  t,
  onTextClick,
}: {
  item: Author;
  t: TFunction;
} & GridCardClickProps) {
  const attachments =
    (item as Author & { attachments?: any[] }).attachments ?? [];
  return (
    <RelatedLinkGridCardShell
      attachments={attachments}
      imageDomain="author"
      imageAlt={`${item.artistesAuteursDescriptionPrenom ?? ""} ${item.artistesAuteursDescriptionNom ?? ""}`.trim()}
      detailPath={`/edit/author/${item.id}`}
      onTextClick={onTextClick}
    >
      <div className="self-stretch min-h-[30px] py-2 flex-col justify-start items-start gap-1 flex">
        <div>
          <span className={mutedLabel}>
            {t("artistesAuteursDescriptionPrenom")}{" "}
          </span>
          <span className={valueStrong}>
            {item.artistesAuteursDescriptionPrenom}
          </span>
        </div>
      </div>
      <div className="self-stretch min-h-[108px] py-2 flex-col justify-start items-start gap-1 flex">
        <p className={`text-start self-stretch ${titleText}`}>
          {item.artistesAuteursDescriptionNom}
        </p>
        <p className={`text-start self-stretch ${bodyText}`}>
          {item.artistesAuteursDescriptionResumeDates}
        </p>
      </div>
    </RelatedLinkGridCardShell>
  );
}
